// src/pages/blog/BlogDetail.jsx
import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";

/* ── Config ─────────────────────────────────────────────────── */
const API_BASE   = process.env.REACT_APP_API_BASE_URL || "http://localhost:3000/api";
const MEDIA_BASE = process.env.REACT_APP_MEDIA_BASE || "http://localhost:3000";

/* ── Icons ──────────────────────────────────────────────────── */
const Ico = {
  arrowLeft:  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M19 12H5" /><path d="M12 19l-7-7 7-7" /></svg>,
  calendar:   <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="4" width="18" height="18" rx="2" /><path d="M16 2v4" /><path d="M8 2v4" /><path d="M3 10h18" /></svg>,
  clock:      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" /></svg>,
  share:      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" /><path d="M8.59 13.51l6.83 3.98" /><path d="M15.41 6.51l-6.82 3.98" /></svg>,
  check:      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M5 12l5 5L19 7" /></svg>,
};

/* ── Helpers ────────────────────────────────────────────────── */
function getAuthorName(author) {
  if (!author) return "JepTags";
  if (typeof author === "string") return author;
  return author.name || "JepTags";
}

function getMediaUrl(url) {
  if (!url) return "";
  if (url.startsWith("http://") || url.startsWith("https://")) return url;
  if (url.startsWith("/uploads/")) return `${MEDIA_BASE}${url}`;
  return `${MEDIA_BASE}/uploads/blogs/${url}`;
}

// Format date as "Nov 29, 2025" — clearer than "11/29/2025"
function formatDate(dateStr) {
  if (!dateStr) return "";
  try {
    return new Date(dateStr).toLocaleDateString(undefined, {
      year: "numeric", month: "short", day: "numeric"
    });
  } catch { return ""; }
}

/* ══════════════════════════════════════════════════════════════ */
export default function BlogDetail() {
  const { slug }   = useParams();
  const navigate   = useNavigate();
  const [blog, setBlog]               = useState(null);
  const [related, setRelated]         = useState([]);
  const [loading, setLoading]         = useState(true);
  const [error, setError]             = useState(null);
  const [copiedShare, setCopiedShare] = useState(false);

  useEffect(() => {
    async function load() {
      try {
        setLoading(true);
        setError(null);

        // Load detail
        const detailRes = await fetch(`${API_BASE}/public/blogs/${slug}`);
        if (!detailRes.ok) {
          setError(detailRes.status === 404 ? "Blog not found" : "Failed to load");
          return;
        }
        const detailJson = await detailRes.json();
        const blogData =
          detailJson.blog ||
          detailJson.data?.blog ||
          detailJson.data ||
          null;
        if (!blogData || blogData.success === false) {
          setError("Blog not found");
          return;
        }
        setBlog(blogData);

        // Load related (non-blocking)
        try {
          const listRes = await fetch(`${API_BASE}/public/blogs`);
          if (listRes.ok) {
            const listJson = await listRes.json();
            const all =
              listJson.blogs ||
              listJson.data?.blogs ||
              (Array.isArray(listJson.data) ? listJson.data : null) ||
              (Array.isArray(listJson) ? listJson : []);
            setRelated((all || []).filter((b) => b.slug !== slug).slice(0, 3));
          }
        } catch {/* related is optional */}
      } catch (err) {
        console.error("Blog load error:", err);
        setError("Failed to load blog post");
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [slug]);

  // Share: copy URL to clipboard
  const handleShare = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopiedShare(true);
      setTimeout(() => setCopiedShare(false), 2000);
    } catch {
      // eslint-disable-next-line no-alert
      prompt("Copy this link:", window.location.href);
    }
  };

  if (loading) {
    return (
      <div className="shop-bd-root">
        <style dangerouslySetInnerHTML={{ __html: STYLES }} />
        <div className="shop-bd-msg">Loading...</div>
      </div>
    );
  }

  if (error || !blog) {
    return (
      <div className="shop-bd-root">
        <style dangerouslySetInnerHTML={{ __html: STYLES }} />
        <div className="shop-bd-msg error">
          {error || "Blog not found"}
          <br /><br />
          <button className="shop-bd-back" onClick={() => navigate("/blog")}>
            <span className="shop-bd-ico-16">{Ico.arrowLeft}</span> All Posts
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="shop-bd-root">
      <style dangerouslySetInnerHTML={{ __html: STYLES }} />

      {/* ARTICLE */}
      <article className="shop-bd-article">
        {/* Back link */}
        <button className="shop-bd-back" onClick={() => navigate("/blog")}>
          <span className="shop-bd-ico-16">{Ico.arrowLeft}</span> All Posts
        </button>

        {blog.category && <span className="shop-bd-cat-badge">{blog.category}</span>}
        <h1>{blog.title}</h1>

        {/* META */}
        <div className="shop-bd-meta">
          <span className="shop-bd-author">{getAuthorName(blog.author)}</span>
          {blog.createdAt && (
            <span className="shop-bd-meta-item">
              <span className="shop-bd-ico-14">{Ico.calendar}</span>
              {formatDate(blog.createdAt)}
            </span>
          )}
          {blog.read_time && (
            <span className="shop-bd-meta-item">
              <span className="shop-bd-ico-14">{Ico.clock}</span>{blog.read_time}
            </span>
          )}
          <button
            className="shop-bd-share-btn"
            onClick={handleShare}
            aria-label="Copy link"
          >
            <span className="shop-bd-ico-16">
              {copiedShare ? Ico.check : Ico.share}
            </span>
            {copiedShare && <span className="shop-bd-share-ok">Copied!</span>}
          </button>
        </div>

        {/* COVER */}
        {blog.coverImage && (
          <img src={getMediaUrl(blog.coverImage)} className="shop-bd-cover" alt={blog.title} />
        )}

        {/* CONTENT */}
        <div
          className="shop-bd-content"
          dangerouslySetInnerHTML={{ __html: blog.content }}
        />

        {/* TAGS */}
        {blog.tags && blog.tags.length > 0 && (
          <div className="shop-bd-tags">
            {blog.tags.map((tag) => (
              <span key={tag} className="shop-bd-tag">#{tag}</span>
            ))}
          </div>
        )}
      </article>

      {/* RELATED */}
      {related.length > 0 && (
        <section className="shop-bd-related">
          <h2>Related <span className="grad">Articles</span></h2>
          <div className="shop-bd-related-grid">
            {related.map((r) => (
              <Link key={r.slug} to={`/blog/${r.slug}`} className="shop-bd-rcard">
                {r.coverImage ? (
                  <img src={getMediaUrl(r.coverImage)} className="shop-bd-rcard-img" alt={r.title} />
                ) : (
                  <div className="shop-bd-rcard-ph"><span>📝</span></div>
                )}
                <div className="shop-bd-rcard-body">
                  <h3>{r.title}</h3>
                  <span className="shop-bd-rcard-read">{r.read_time || "5 min read"}</span>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* FLOATING SHARE BUTTON */}
      <button className="shop-bd-float-share" onClick={handleShare} aria-label="Share">
        {copiedShare ? Ico.check : Ico.share}
      </button>
    </div>
  );
}

/* ══════════════════════════════════════════════════════════════ */
const STYLES = `
.shop-bd-root *,.shop-bd-root *::before,.shop-bd-root *::after{margin:0;padding:0;box-sizing:border-box}
.shop-bd-root{
  --cyan:#00E5CC;--cyan-bright:#00FFE0;--teal:#22D97F;
  --navy-deep:#030a14;--navy:#0a1628;--navy-mid:#0F2140;
  font-family:'Sora',sans-serif;background:var(--navy-deep);color:#e0f0f0;min-height:100vh;padding-bottom:100px;
}
.shop-bd-root a{text-decoration:none;color:inherit}
.grad{background:linear-gradient(135deg,var(--cyan-bright),var(--cyan),var(--teal));-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text}
.jep{color:var(--cyan)}.tags{color:var(--teal)}
.shop-bd-ico-16 svg{width:16px;height:16px}.shop-bd-ico-14 svg{width:14px;height:14px}

/* BACK BUTTON */
.shop-bd-back{display:inline-flex;align-items:center;gap:6px;background:none;border:1px solid rgba(0,229,204,0.15);color:rgba(224,240,240,0.6);font-family:'Sora',sans-serif;font-size:13px;padding:8px 16px;border-radius:8px;cursor:pointer;transition:all .3s;margin-bottom:24px}
.shop-bd-back:hover{border-color:var(--cyan);color:var(--cyan)}

/* MSG */
.shop-bd-msg{text-align:center;padding:60px 24px;color:rgba(224,240,240,0.4);font-size:16px}
.shop-bd-msg.error{color:#f87171}

/* ARTICLE */
.shop-bd-article{max-width:800px;margin:0 auto;padding:48px 24px 0}

.shop-bd-cat-badge{display:inline-block;padding:6px 16px;border-radius:100px;background:rgba(0,229,204,0.08);border:1px solid rgba(0,229,204,0.15);font-size:11px;font-weight:600;letter-spacing:1px;text-transform:uppercase;color:var(--cyan);margin-bottom:16px}

.shop-bd-article h1{font-size:clamp(28px,5vw,48px);font-weight:800;line-height:1.15;letter-spacing:-1.5px;margin-bottom:20px}

/* META */
.shop-bd-meta{display:flex;align-items:center;gap:20px;flex-wrap:wrap;margin-bottom:32px;font-size:13px;color:rgba(224,240,240,0.4)}
.shop-bd-author{font-weight:600;color:rgba(224,240,240,0.6)}
.shop-bd-meta-item{display:flex;align-items:center;gap:6px}
.shop-bd-share-btn{position:relative;margin-left:auto;background:none;border:1px solid rgba(0,229,204,0.15);border-radius:8px;padding:8px;cursor:pointer;color:rgba(224,240,240,0.4);transition:all .3s;display:flex;align-items:center;gap:8px}
.shop-bd-share-btn:hover{border-color:var(--cyan);color:var(--cyan)}
.shop-bd-share-ok{font-size:12px;padding-right:6px;color:var(--teal)}

/* COVER */
.shop-bd-cover{width:100%;max-height:520px;object-fit:cover;border-radius:20px;border:1px solid rgba(0,229,204,0.06);margin-bottom:40px;box-shadow:0 20px 60px rgba(0,0,0,0.3)}

/* CONTENT */
.shop-bd-content{font-size:16px;line-height:1.8;color:rgba(224,240,240,0.65)}
.shop-bd-content h1,.shop-bd-content h2,.shop-bd-content h3,.shop-bd-content h4{color:#e0f0f0;font-weight:700;margin-top:32px;margin-bottom:12px}
.shop-bd-content h2{font-size:24px}.shop-bd-content h3{font-size:20px}
.shop-bd-content p{margin-bottom:16px}
.shop-bd-content a{color:var(--cyan);text-decoration:underline;text-underline-offset:3px}
.shop-bd-content a:hover{color:var(--cyan-bright)}
.shop-bd-content ul,.shop-bd-content ol{padding-left:24px;margin-bottom:16px}
.shop-bd-content li{margin-bottom:8px}
.shop-bd-content blockquote{border-left:3px solid var(--cyan);padding:16px 20px;margin:24px 0;background:rgba(0,229,204,0.04);border-radius:0 12px 12px 0;font-style:italic;color:rgba(224,240,240,0.5)}
.shop-bd-content img{max-width:100%;border-radius:12px;margin:24px 0}
.shop-bd-content code{background:rgba(0,229,204,0.08);padding:2px 6px;border-radius:4px;font-size:14px;font-family:'JetBrains Mono',monospace}
.shop-bd-content pre{background:rgba(15,33,64,0.8);border:1px solid rgba(0,229,204,0.08);border-radius:12px;padding:20px;overflow-x:auto;margin:24px 0}
.shop-bd-content pre code{background:none;padding:0}
.shop-bd-content table{width:100%;border-collapse:collapse;margin:24px 0}
.shop-bd-content th,.shop-bd-content td{padding:10px 14px;border:1px solid rgba(0,229,204,0.08);text-align:left;font-size:14px}
.shop-bd-content th{background:rgba(0,229,204,0.06);font-weight:700}

/* TAGS */
.shop-bd-tags{display:flex;flex-wrap:wrap;gap:10px;margin-top:40px}
.shop-bd-tag{padding:6px 16px;border-radius:100px;background:rgba(0,229,204,0.06);border:1px solid rgba(0,229,204,0.1);color:rgba(224,240,240,0.5);font-size:13px}

/* RELATED */
.shop-bd-related{max-width:1100px;margin:80px auto 0;padding:0 24px}
.shop-bd-related h2{font-size:28px;font-weight:800;margin-bottom:28px}
.shop-bd-related-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:20px}
.shop-bd-rcard{border-radius:16px;background:rgba(15,33,64,0.5);border:1px solid rgba(0,229,204,0.06);overflow:hidden;transition:all .4s}
.shop-bd-rcard:hover{border-color:rgba(0,229,204,0.15);transform:translateY(-4px)}
.shop-bd-rcard-img{width:100%;height:180px;object-fit:cover}
.shop-bd-rcard-ph{height:180px;background:rgba(0,229,204,0.04);display:flex;align-items:center;justify-content:center;font-size:32px}
.shop-bd-rcard-body{padding:20px}
.shop-bd-rcard-body h3{font-size:16px;font-weight:700;line-height:1.4;margin-bottom:8px}
.shop-bd-rcard-read{font-size:12px;color:rgba(224,240,240,0.3)}

/* FLOAT SHARE */
.shop-bd-float-share{position:fixed;bottom:28px;right:28px;width:52px;height:52px;border-radius:50%;background:linear-gradient(135deg,var(--cyan-bright),var(--teal));border:none;cursor:pointer;display:flex;align-items:center;justify-content:center;box-shadow:0 8px 32px rgba(0,229,204,0.35);z-index:150;transition:transform .3s}
.shop-bd-float-share:hover{transform:scale(1.1)}
.shop-bd-float-share svg{width:22px;height:22px;color:var(--navy-deep);stroke:var(--navy-deep)}

/* RESPONSIVE */
@media(max-width:900px){
  .shop-bd-article{padding:36px 20px 0}
  .shop-bd-related-grid{grid-template-columns:1fr 1fr}
}
@media(max-width:600px){
  .shop-bd-related-grid{grid-template-columns:1fr}
}
`;